/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type {
  ItemFocusDetail,
  ItemHoverDetail,
  ItemInfo,
  PieChartItemData
} from './pieChart.types';

/**
 * Returns the item based on item info in a pie chart.
 * @param itemInfo
 * @param items
 * @returns
 */
function getItem<K, D extends PieChartItemData<K>>(itemInfo: ItemInfo, items: D[]) {
  const { itemIndex } = itemInfo;
  return items[itemIndex];
}

/**
 * Returns the nav util for pie chart.
 * @param data
 * @param isRtl
 * @returns
 */
export function getPieChartNavUtils<K, D extends PieChartItemData<K>>(data: D[], isRtl: boolean) {
  return {
    getNextChartItem: (
      { itemIndex }: { itemIndex: number },
      arrow: 'ArrowLeft' | 'ArrowRight' | 'ArrowDown' | 'ArrowUp'
    ): { itemIndex: number } => {
      let newItemIndex = itemIndex;
      if (
        (isRtl && (arrow === 'ArrowRight' || arrow === 'ArrowUp')) ||
        (!isRtl && (arrow === 'ArrowLeft' || arrow === 'ArrowDown'))
      ) {
        newItemIndex = itemIndex - 1;
      } else {
        newItemIndex = itemIndex + 1;
      }
      return {
        itemIndex: ((newItemIndex % data.length) + data.length) % data.length
      };
    },
    getDetailFromInfo: (itemInfo?: ItemInfo): ItemHoverDetail<K, D> | ItemFocusDetail<K, D> => {
      if (!itemInfo) {
        return { id: undefined, data: undefined };
      }
      const item = getItem(itemInfo, data);
      return { id: item ? item.id : undefined, data: item };
    }
  };
}
